/**
 * SavedFilters — a "Saved views" dropdown for the FilterBar. Lists the user's
 * own + shared filter sets, applies one on click, saves the current filter
 * under a name, and deletes own sets.
 *
 * Generic: the parent supplies `current` (the filter object to persist) and
 * `onApply(filters)` (how to apply a recalled set). The framework never
 * assumes what's inside the blob.
 */
import React, { useState } from 'react';
import { Bookmark, Trash2, Check, Plus } from 'lucide-react';
import { useSavedFilters } from '../hooks/useSavedFilters';

export default function SavedFilters({ module, current, onApply }) {
  const { filters, save, remove } = useSavedFilters(module);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [shared, setShared] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      await save(trimmed, current || {}, shared);
      setName('');
      setShared(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
      >
        <Bookmark className="h-4 w-4" /> Saved views
      </button>

      {open && (
        <div className="absolute right-0 z-20 mt-1 w-72 rounded-md border border-gray-200 bg-white p-2 shadow-lg">
          <div className="max-h-56 overflow-y-auto">
            {filters.length === 0 && (
              <div className="px-2 py-3 text-sm text-gray-400">No saved views yet</div>
            )}
            {filters.map((f) => (
              <div key={f.id} className="group flex items-center justify-between rounded px-2 py-1.5 hover:bg-gray-50">
                <button
                  type="button"
                  className="flex-1 text-left text-sm text-gray-800"
                  onClick={() => { onApply?.(f.filters || {}); setOpen(false); }}
                >
                  {f.name}
                  {f.is_shared && <span className="ml-2 text-[10px] uppercase text-gray-400">shared</span>}
                  {!f.is_owner && <span className="ml-2 text-[10px] uppercase text-gray-400">org</span>}
                </button>
                {f.is_owner && (
                  <button
                    type="button"
                    className="ml-2 rounded p-1 text-gray-300 hover:text-red-600 group-hover:text-gray-500"
                    title="Delete"
                    onClick={() => remove(f.id)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="mt-2 border-t border-gray-100 pt-2">
            <div className="flex items-center gap-1">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                placeholder="Save current as…"
                className="min-w-0 flex-1 rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none"
              />
              <button
                type="button"
                onClick={handleSave}
                disabled={busy || !name.trim()}
                className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-2 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {busy ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
              </button>
            </div>
            <label className="mt-1 flex cursor-pointer items-center gap-1 px-1 text-xs text-gray-500">
              <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} />
              Share with everyone in the org
            </label>
          </div>
        </div>
      )}
    </div>
  );
}
